/* MoonCommerce festive snowfall. Decorative only: pointer-events none,
   off for reduced motion, paused in background tabs, viewer can switch it off.
   Three depth layers (far, mid, near) and gusting wind. Nothing here
   listens to page scroll, so the overlay costs nothing while the page moves. */
(() => {
  if (window.mcLuxSnow) return;
  const STORE_KEY = 'mcLuxSnowOff';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = [];

  const readOff = () => {
    try { return sessionStorage.getItem(STORE_KEY) === '1'; } catch (e) { return false; }
  };
  const writeOff = (off) => {
    try { off ? sessionStorage.setItem(STORE_KEY, '1') : sessionStorage.removeItem(STORE_KEY); } catch (e) {}
  };

  let off = readOff();

  // Flake sprites: a few irregular clumps so no two flakes read as identical dots.
  // The faint cool edge keeps white flakes visible on white sections.
  const makeSprite = (blur, seed) => {
    const size = 64;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d');
    if (blur) ctx.filter = `blur(${blur}px)`;
    const rand = (n) => {
      const x = Math.sin(seed * 9301 + n * 49297) * 233280;
      return x - Math.floor(x);
    };
    const lobes = blur ? 1 : 4 + Math.floor(rand(1) * 3);
    for (let i = 0; i < lobes; i++) {
      const a = rand(i + 2) * Math.PI * 2;
      const d = i === 0 ? 0 : 3 + rand(i + 7) * 5;
      const r = i === 0 ? 13 : 7 + rand(i + 11) * 5;
      const x = size / 2 + Math.cos(a) * d;
      const y = size / 2 + Math.sin(a) * d;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(.55, 'rgba(250,252,255,.9)');
      g.addColorStop(.82, 'rgba(178,192,212,.6)');
      g.addColorStop(1, 'rgba(160,176,196,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    return c;
  };
  const crisp = [1, 2, 3, 4].map((s) => makeSprite(0, s));
  const soft = [makeSprite(3, 9), makeSprite(5, 10)];

  // Layer recipes: share of flakes, size range (px), fall speed (px/s), sway, opacity.
  const LAYERS = [
    { share: .5, size: [1.5, 2.8], speed: [14, 26], sway: 8, alpha: [.6, .85], depth: .16, sprites: crisp },
    { share: .4, size: [3, 5.4], speed: [30, 52], sway: 16, alpha: [.85, 1], depth: .4, sprites: crisp },
    { share: .1, size: [10, 18], speed: [64, 96], sway: 30, alpha: [.3, .5], depth: .88, sprites: soft }
  ];
  const between = ([a, b]) => a + Math.random() * (b - a);

  class Snow {
    constructor(canvas) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.page = canvas.dataset.mcSnow === 'page';
      this.density = Number(canvas.dataset.density) || 60;
      this.flakes = [];
      this.visible = true;
      this.frame = null;
      this.last = 0;
      this.time = Math.random() * 100;
      this.tick = this.tick.bind(this);
      this.resize();
      if (this.page) window.addEventListener('resize', () => this.resize(), { passive: true });
      else if ('ResizeObserver' in window) new ResizeObserver(() => this.resize()).observe(canvas.parentElement);
      if (!this.page && 'IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          this.sync();
        }).observe(canvas);
      }
      this.sync();
    }

    resize() {
      const parent = this.canvas.parentElement;
      const w = this.page ? window.innerWidth : parent.clientWidth;
      // Mobile browsers change innerHeight as the address bar slides away.
      // Size to the tallest viewport so that never rebuilds the canvas.
      const h = this.page ? Math.max(window.innerHeight, window.screen?.height || 0) : parent.clientHeight;
      if (!w || !h) return;
      if (w === this.w && h <= this.h) return;
      const ratio = Math.min(window.devicePixelRatio || 1, this.page ? 1.25 : 1.5);
      this.w = w;
      this.h = h;
      this.canvas.width = Math.round(w * ratio);
      this.canvas.height = Math.round(h * ratio);
      if (this.page) this.canvas.style.height = h + 'px';
      this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const target = Math.round(this.density * Math.min(1.25, Math.max(.5, w / 1280)));
      while (this.flakes.length < target) this.flakes.push(this.make(true));
      this.flakes.length = target;
    }

    make(anywhere) {
      const roll = Math.random();
      const layer = roll < LAYERS[0].share ? LAYERS[0] : roll < LAYERS[0].share + LAYERS[1].share ? LAYERS[1] : LAYERS[2];
      const size = between(layer.size);
      return {
        layer,
        sprite: layer.sprites[Math.floor(Math.random() * layer.sprites.length)],
        x: Math.random() * this.w,
        y: anywhere ? Math.random() * this.h : -size * 2,
        size,
        vy: between(layer.speed),
        sway: layer.sway * (.6 + Math.random() * .8),
        freq: .35 + Math.random() * .7,
        phase: Math.random() * Math.PI * 2,
        alpha: between(layer.alpha),
        age: anywhere ? 1 : 0
      };
    }

    sync() {
      const run = !off && !reduce.matches && this.visible && !document.hidden;
      this.canvas.hidden = off || reduce.matches;
      if (run && !this.frame) {
        this.last = performance.now();
        this.frame = requestAnimationFrame(this.tick);
      } else if (!run && this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    tick(now) {
      const dt = Math.min((now - this.last) / 1000, .05);
      this.last = now;
      this.time += dt;
      const t = this.time;
      // Slow gusting breeze: a few out-of-phase waves, mostly drifting one way.
      const wind = 7 + Math.sin(t * .13) * 12 + Math.sin(t * .37 + 1.7) * 6 + Math.sin(t * .9 + .4) * 2.5;

      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.w, this.h);
      for (let i = 0; i < this.flakes.length; i++) {
        const f = this.flakes[i];
        const depth = f.layer.depth;
        f.age = Math.min(1, f.age + dt * 1.5);
        f.y += f.vy * dt;
        f.x += (wind * (.5 + depth) + Math.sin(t * f.freq + f.phase) * f.sway) * dt;
        if (f.y > this.h + f.size * 2) { this.flakes[i] = this.make(false); continue; }
        if (f.y < -f.size * 3) f.y = this.h + f.size;
        if (f.x < -f.size * 2) f.x = this.w + f.size;
        if (f.x > this.w + f.size * 2) f.x = -f.size;
        const d = f.size * 2.2;
        ctx.globalAlpha = f.alpha * f.age;
        ctx.drawImage(f.sprite, f.x - d / 2, f.y - d / 2, d, d);
      }
      ctx.globalAlpha = 1;
      this.frame = requestAnimationFrame(this.tick);
    }
  }

  const syncButtons = () => {
    document.querySelectorAll('[data-mc-snow-toggle]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(!off));
      const label = btn.querySelector('[data-mc-snow-label]');
      if (label) label.textContent = off ? btn.dataset.labelOn : btn.dataset.labelOff;
    });
  };

  // "[days]" countdowns, e.g. days to Christmas, kept current between page caches.
  const updateCountdowns = () => {
    document.querySelectorAll('[data-mc-countdown]').forEach((el) => {
      const target = new Date(el.dataset.mcCountdown + 'T00:00:00');
      const days = Math.ceil((target - new Date()) / 86400000);
      if (days > 0) el.textContent = days;
    });
  };

  const init = () => {
    document.querySelectorAll('canvas[data-mc-snow]:not([data-mc-snow-ready])').forEach((canvas) => {
      canvas.dataset.mcSnowReady = '';
      instances.push(new Snow(canvas));
    });
    syncButtons();
    updateCountdowns();
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-mc-snow-toggle]');
    if (!btn) return;
    off = !off;
    writeOff(off);
    instances.forEach((s) => s.sync());
    syncButtons();
  });
  document.addEventListener('visibilitychange', () => instances.forEach((s) => s.sync()));
  reduce.addEventListener?.('change', () => instances.forEach((s) => s.sync()));
  document.addEventListener('shopify:section:load', init);

  window.mcLuxSnow = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
